import { sanitizeSegment } from "../../storage/fsa.ts";
import { shortHash } from "../../storage/hash.ts";
import type { Chat } from "./types.ts";

/**
 * Output tree for Teams (ARCHITECTURE.md §7):
 *
 *   teams/<chat-key>/index.html
 *   teams/<chat-key>/messages/<messageId>.json
 *   teams/<chat-key>/assets/<hash>.<ext>
 */

const MAX_NAME_LENGTH = 60;

/** A readable label for a chat — topic, or the other participants' names. */
export function chatLabel(chat: Chat, selfUserId: string | undefined): string {
    if (chat.topic) {
        return chat.topic;
    }

    const others = (chat.members ?? [])
        .filter((m) => !selfUserId || m.userId !== selfUserId)
        .map((m) => m.displayName)
        .filter((name): name is string => Boolean(name));

    if (others.length > 0) {
        return others.join(", ");
    }

    return chat.chatType === "meeting" ? "Meeting" : "Chat";
}

/**
 * Directory name for one chat: a readable label plus a hash of the chat id.
 *
 * The hash suffix is load-bearing, not decorative — topics collide easily
 * ("Project X" is a common name for more than one chat), and without it two
 * different chats could resolve to the same folder and silently merge.
 */
export async function chatKey(chat: Chat, selfUserId: string | undefined): Promise<string> {
    const label = sanitizeSegment(chatLabel(chat, selfUserId), MAX_NAME_LENGTH);
    const suffix = await shortHash(chat.id, 8);
    return `${label || "chat"}_${suffix}`;
}

export function messagePath(key: string, messageId: string): string {
    return `teams/${key}/messages/${sanitizeSegment(messageId)}.json`;
}

export function chatIndexPath(key: string): string {
    return `teams/${key}/index.html`;
}

export function chatMessagesDirPath(key: string): string {
    return `teams/${key}/messages`;
}

/**
 * Asset filename is a hash of its source URL, per §8 — resume-friendly (the
 * same URL always names the same file, so a re-fetch after an interruption
 * overwrites rather than duplicates) and stable across runs.
 *
 * Callers hash the URL once via this function, then derive both the on-disk
 * path and the href used inside index.html from the same hash — never hash
 * twice, or the two could theoretically diverge if the algorithm ever changes.
 */
export async function assetHash(sourceUrl: string): Promise<string> {
    return shortHash(sourceUrl, 16);
}

export function assetDiskPath(key: string, hash: string, extension: string): string {
    return `teams/${key}/assets/${hash}${extension}`;
}

/** Relative path from a chat's index.html to one of its assets. */
export function assetRelativePath(hash: string, extension: string): string {
    return `assets/${hash}${extension}`;
}
